import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import { 
  getFirestore, 
  initializeFirestore,
  memoryLocalCache,
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  addDoc, 
  updateDoc, 
  onSnapshot, 
  query, 
  where,
  deleteDoc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import firebaseConfigJson from '../../firebase-applet-config.json';
import { UserProfile, PackageItem, ShipmentOrder, UserRole, AppNotification, SupportTicket, PersonalShopperOrder, BlogPost, YahooAuctionBid } from '../types';

const firebaseConfig = {
  apiKey: firebaseConfigJson.apiKey,
  authDomain: firebaseConfigJson.authDomain,
  projectId: firebaseConfigJson.projectId,
  storageBucket: firebaseConfigJson.storageBucket,
  messagingSenderId: firebaseConfigJson.messagingSenderId,
  appId: firebaseConfigJson.appId,
};

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

const customDbId = (firebaseConfigJson as Record<string, string>).firestoreDatabaseId || '(default)';

// Initialize Firestore with in-memory local cache to prevent multi-tab primary lease lock contention
let dbInstance;
try {
  dbInstance = initializeFirestore(app, {
    localCache: memoryLocalCache(),
    experimentalAutoDetectLongPolling: true
  }, customDbId);
} catch (e) {
  dbInstance = getFirestore(app, customDbId);
}

export const db = dbInstance;
export const auth = getAuth(app);

// Helper constants for Japan Warehouse Address
export const JAPAN_WAREHOUSE_BASE = {
  postalCode: '350-1151',
  prefecture: 'Saitama-ken (埼玉県)',
  city: 'Kawagoe-shi (川越市)',
  addressLine1: 'Imafuku 766-9',
  phone: '080-4859-1502',
  country: 'Japan (日本)',
  recipientBase: 'Do Japao'
};

// Master Admin User Definition (always protected)
export const MASTER_ADMIN_USER: UserProfile = {
  uid: 'admin-egami-001',
  email: 'egami.corporation@gmail.com',
  password: 'Reiko.8879',
  role: 'admin',
  suiteNumber: 'DJ-ADMIN',
  name: 'Depósito Do Japão (Admin)',
  phone: '080-4859-1502',
  emailVerified: true,
  createdAt: new Date().toISOString()
};

// Production Default Admin User (Client suites, stock packages, and shipments start completely empty)
export const INITIAL_DEMO_USERS: UserProfile[] = [
  MASTER_ADMIN_USER
];

export const INITIAL_DEMO_PACKAGES: PackageItem[] = [];

export const INITIAL_DEMO_SHIPMENTS: ShipmentOrder[] = [];
export const INITIAL_DEMO_NOTIFICATIONS: AppNotification[] = [];
export const INITIAL_DEMO_TICKETS: SupportTicket[] = [];
export const INITIAL_DEMO_PERSONAL_SHOPPER: PersonalShopperOrder[] = [];
export const INITIAL_DEMO_YAHOO_AUCTION_BIDS: YahooAuctionBid[] = [];

export const INITIAL_DEMO_BLOG_POSTS: BlogPost[] = [
  {
    id: 'post-1',
    title: 'Como Comprar no Mercari Japan e Enviar para o Brasil',
    slug: 'como-comprar-no-mercari-japan',
    excerpt: 'Descubra como encontrar figures raros, roupas de grife e colecionáveis no maior brechó online do Japão com a nossa Suíte.',
    content: `O **Mercari Japan** (メルカリ) é o maior marketplace P2P (pessoa para pessoa) do Japão. Lá você encontra desde figures raros de anime e mangás antigos até roupas vintage, peças de videogame clássico e gadgets exclusivos por preços inacreditáveis!

### Por que os produtos do Mercari são tão baratos?
Os japoneses são extremamente cuidadosos com a conservação de seus objetos. Um item marcado como "usado" (中古) no Mercari frequentemente chega parecendo novinho em folha, com embalagem original e manuais intactos!

### Passo a passo para comprar no Mercari com a Do Japão:
1. **Acesse o site ou app do Mercari JP**: Navegue usando a tradução do navegador em português ou inglês.
2. **Copie o Link do Produto**: Encontrou o que procurava? Copie a URL da página do item.
3. **Solicite a Compra pelo Redirecionamento ou Comprador Pessoal**:
   - Se o vendedor aceitar envio para o nosso endereço de Saitama, você pode pagar direto no cartão de crédito/paypal e colocar o endereço da sua **Suíte Do Japão**.
   - Se precisar de intermediação (vendedor exige pagamento local ou cartão japonês), utilize nosso serviço de **Comprador Pessoal (Personal Shopper)** no painel!
4. **Receba no Nosso Depósito**: Assim que o pacote chegar ao nosso depósito, nós tiramos fotos e cadastramos o peso na sua Suíte para você solicitar a consolidação.

### Dica de Ouro Do Japão 💡
No Mercari, preste atenção na reputação do vendedor (estrelas) e se o valor já inclui o frete interno no Japão (**送料込み** - Shipping Included). Se tiver **着払い** (Pay on Delivery), a taxa de entrega local será cobrada na chegada do depósito.`,
    coverImage: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1000&auto=format&fit=crop&q=80',
    category: 'Mercari JP',
    tags: ['Mercari', 'Colecionáveis', 'Anime', 'Economia', 'Guia'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 5,
    published: true,
    featured: true,
    views: 1240,
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: 'post-2',
    title: 'Guia Completo: EMS vs Small Packet Airmail vs Frete Navio (Surface)',
    slug: 'guia-comparativo-fretes-japao-brasil',
    excerpt: 'Qual opção de frete vale mais a pena? Analisamos prazos, limites de peso e custos para você economizar ao máximo.',
    content: `Escolher a modalidade correta de envio do Japão para o Brasil pode salvar centenas de reais no custo total do seu pedido. A **Do Japão** oferece os principais métodos oficiais do **Japan Post** e transportadoras internacionais.

### 1. Japan Post - EMS (Express Mail Service)
- **Velocidade**: Média de 7 a 15 dias úteis para o Brasil.
- **Rastreio**: Completo ponto a ponto (de Tóquio até sua porta via Correios do Brasil).
- **Ideal para**: Pacotes valiosos, eletrônicos, figures delicados ou quando você tem pressa.
- **Limite de Peso**: Até 30 kg por caixa.

### 2. Small Packet (Airmail registrado)
- **Velocidade**: Média de 12 a 25 dias úteis.
- **Preço**: Consideravelmente mais barato que o EMS para itens leves.
- **Limite de Peso**: Máximo de 2,0 kg por pacote.
- **Ideal para**: Roupas, cosméticos, snacks japoneses, pequenos jogos e chaveiros.

### 3. Frete Marítimo / Navio (Surface Mail)
- **Velocidade**: 60 a 90 dias úteis.
- **Preço**: O menor custo por quilo do mercado.
- **Ideal para**: Lotes volumosos, caixas pesadas (acima de 10kg), livros, autopeças ou coleções completas sem urgência.

### Nosso Serviço de Consolidação & Redução de Caixa
Na sua Suíte Do Japão, você pode juntar pacotes de várias lojas diferentes (Amazon JP, Rakuten, Surugaya) em uma única caixa otimizada. Nossa equipe remove caixas externas desnecessárias para garantir que seu pacote fique no menor peso e tamanho possível!`,
    coverImage: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1000&auto=format&fit=crop&q=80',
    category: 'Dicas de Frete',
    tags: ['Frete', 'EMS', 'Japan Post', 'Correios', 'Economia'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 7,
    published: true,
    featured: true,
    views: 980,
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: 'post-3',
    title: 'Como Arrematar no Yahoo! Auctions Japan Sem Passar Raiva',
    slug: 'como-arrematar-no-yahoo-auctions-japan',
    excerpt: 'Aprenda os segredos dos leilões japoneses: como dar lances no último minuto e arrematar preciosidades relíquia.',
    content: `O **Yahoo! Auctions Japan** (ヤフオク!) é o maior portal de leilões do Japão. É lá que colecionadores do mundo inteiro procuram relíquias como relógios Seiko/G-Shock raros, guitarras Fender Japan, cards de Pokémon primeira edição e games retro.

### Dicas para arrematar com sucesso:
1. **Verifique a Reputação do Vendedor**: Dê preferência a vendedores com taxa de aprovação superior a 98% e mais de 100 avaliações positivas (良い).
2. **Atenção ao Horário de Encerramento**: Os leilões no Japão encerram no horário local de Tóquio (JST - UTC+9). Verifique a conversão para o fuso do Brasil!
3. **Extensão Automática (Automatic Extension)**: A maioria dos leilões do Yahoo! estende o prazo por +5 minutos se houver um lance nos minutos finais.
4. **Use nosso Serviço de Comprador Pessoal**: Se você não possui cartão de crédito do Japão para dar lances diretamente no Yahoo! Auctions, nossa equipe faz o lance por você com o valor máximo desejado!`,
    coverImage: 'https://images.unsplash.com/photo-1579226905180-636b76d96082?w=1000&auto=format&fit=crop&q=80',
    category: 'Yahoo Auctions',
    tags: ['Yahoo Auctions', 'Leilão', 'Relíquias', 'Retro Games'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 4,
    published: true,
    featured: false,
    views: 650,
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: 'post-4',
    title: 'Top 10 Produtos em Alta no Japão Mais Procurados para Importar em 2026',
    slug: 'top-10-produtos-em-alta-no-japao-2026',
    excerpt: 'De action figures exclusivas a eletrônicos retro e cosméticos virais: confira o que está bombando no e-commerce japonês.',
    content: `O mercado consumidor japonês lança constantemente itens exclusivos, colecionáveis de edição limitada e inovações que viram febre no mundo inteiro. Se você quer estar à frente das tendências e importar o que há de melhor, preparamos o ranking definitivo dos **Produtos em Alta no Japão** neste momento!

### 1. Action Figures e Estátuas de Edição Limitada (Bandai / Good Smile)
Action figures das franquias *Dragon Ball*, *One Piece*, *Demon Slayer* e *Jujutsu Kaisen* lançados exclusivamente via Ichiban Kuji (一番くじ) ou Premium Bandai continuam sendo os campeões de buscas no Mercari JP.

### 2. Consoles e Games Retro Conservados (Game Boy, PS2, Famicom)
Lojas de Akihabara e leilões do Yahoo! Auctions oferecem videogames antigos em excelente estado de conservação, com caixa original e manuais sem rasuras por frações do preço cobrado no Brasil.

### 3. Câmeras e Lentes Fotográficas Vintage (Canon, Nikon, Fujifilm)
Câmeras digitais de bolso dos anos 2000 (Digicams Y2K) e lentes analógicas japonesas estão super em alta entre fotógrafos e criadores de conteúdo.

### 4. Roupas e Streetwear Japonês (BAPE, Evisu, Comme des Garçons)
Roupas de grife e peças vintage de streetwear japonês encontradas no Mercari JP são negociadas por ótimos preços devido ao câmbio favorável do Iene.

### 5. Snacking & Utensílios de Cozinha Japoneses
Doces temáticos sazonais (KitKat sabor Matcha, Sakura, Batata Doce) e facas de chef artesanais de Seki e Sakai.

---

### Como Comprar Produtos em Alta com a Do Japão:
Basta copiar o link do produto no Mercari, Yahoo! ou Amazon JP e solicitar pelo nosso serviço de **Comprador Pessoal** ou usar o endereço da sua **Suíte Exclusiva** em Saitama!`,
    coverImage: 'https://images.unsplash.com/photo-1607604276583-eef5d076aa5f?w=1000&auto=format&fit=crop&q=80',
    category: 'Produtos em Alta',
    tags: ['Produtos em Alta', 'Trends', 'Japan', 'Mercari JP', 'Colecionáveis', '2026'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 6,
    published: true,
    featured: true,
    views: 1890,
    createdAt: new Date().toISOString()
  }
];

let isSeedingProcessRunning = false;

// Always ensure the master admin user is present and purge any duplicates
export async function ensureAdminUserExists(): Promise<UserProfile> {
  try {
    const adminRef = doc(db, 'users', MASTER_ADMIN_USER.uid);
    const snap = await getDoc(adminRef);
    if (!snap.exists()) {
      await setDoc(adminRef, MASTER_ADMIN_USER);
      console.log('✅ Conta de administrador restaurada com sucesso no Firestore.');
    } else {
      // Ensure role is admin and secure password
      const currentData = snap.data();
      if (
        currentData.role !== 'admin' ||
        currentData.suiteNumber !== 'DJ-ADMIN' ||
        !currentData.password ||
        currentData.password === 'teste12345' ||
        currentData.password === 'admin123'
      ) {
        await setDoc(
          adminRef,
          {
            ...currentData,
            role: 'admin',
            suiteNumber: 'DJ-ADMIN',
            password: MASTER_ADMIN_USER.password
          },
          { merge: true }
        );
      }
    }

    // Check for and purge any duplicate user documents with admin email
    try {
      const usersSnap = await getDocs(collection(db, 'users'));
      const batch = writeBatch(db);
      let foundDups = false;
      usersSnap.forEach((d) => {
        if (d.id !== MASTER_ADMIN_USER.uid) {
          const data = d.data();
          const emailLower = data.email?.toLowerCase();
          if (emailLower === MASTER_ADMIN_USER.email.toLowerCase()) {
            console.log(`🧹 Removendo conta duplicada de admin (${d.id}) no Firestore.`);
            batch.delete(d.ref);
            foundDups = true;
          }
        }
      });
      if (foundDups) {
        await batch.commit();
      }
    } catch (cleanErr) {
      console.warn('Notice cleaning duplicate admin accounts:', cleanErr);
    }
  } catch (err) {
    console.warn('Notice ensuring admin user in Firestore:', err);
  }
  return MASTER_ADMIN_USER;
}

// Initialize Firestore Collections with default data if empty (using writeBatch for efficiency)
export async function seedInitialDataIfNeeded() {
  // Always verify and restore master admin
  await ensureAdminUserExists();

  if (typeof window !== 'undefined' && localStorage.getItem('app_dojapao_seeded_v4') === 'true') return;
  if (isSeedingProcessRunning) return;

  isSeedingProcessRunning = true;
  try {
    const batch = writeBatch(db);
    let hasNewData = false;

    // Check and seed missing users
    for (const u of INITIAL_DEMO_USERS) {
      const uSnap = await getDoc(doc(db, 'users', u.uid));
      if (!uSnap.exists()) {
        batch.set(doc(db, 'users', u.uid), u);
        hasNewData = true;
      }
    }

    // Check and seed missing packages
    for (const p of INITIAL_DEMO_PACKAGES) {
      const pSnap = await getDoc(doc(db, 'packages', p.id));
      if (!pSnap.exists()) {
        batch.set(doc(db, 'packages', p.id), p);
        hasNewData = true;
      }
    }

    // Check and seed missing shipments
    for (const s of INITIAL_DEMO_SHIPMENTS) {
      const sSnap = await getDoc(doc(db, 'shipments', s.id));
      if (!sSnap.exists()) {
        batch.set(doc(db, 'shipments', s.id), s);
        hasNewData = true;
      }
    }

    // Check and seed missing blog posts
    for (const post of INITIAL_DEMO_BLOG_POSTS) {
      const bSnap = await getDoc(doc(db, 'blog_posts', post.id));
      if (!bSnap.exists()) {
        batch.set(doc(db, 'blog_posts', post.id), post);
        hasNewData = true;
      }
    }

    if (hasNewData) {
      await batch.commit();
    }
    if (typeof window !== 'undefined') localStorage.setItem('app_dojapao_seeded_v4', 'true');
  } catch (err: any) {
    if (typeof window !== 'undefined') localStorage.setItem('app_dojapao_seeded_v4', 'true');
    console.warn('Firestore seed check notice (using local state):', err?.message || err);
  } finally {
    isSeedingProcessRunning = false;
  }
}
